import React, { useState } from 'react';
import { Package, TrendingUp, Truck, ArrowDownLeft, RefreshCw, AlertTriangle, Eye, ChevronRight } from 'lucide-react';

const WarehouseMainDashboard = ({
    stats = {},
    loadingRequests = [],
    offloadRequests = [],
    transferHistory = [],
    onViewRequest,
    onNavigateView
}) => {
    const [hoveredSegment, setHoveredSegment] = useState(null);

    // Dynamic Consolidated Calculation from Real Lists (Zero Mock Mismatches)
    const pendingLoadingList = loadingRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'SUBMITTED' || s === 'PENDING APPROVAL' || s === 'PENDING';
    });

    const pendingOffloadList = offloadRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'SUBMITTED' || s === 'PENDING RETURN' || s === 'UNDER VERIFICATION' || s === 'PENDING';
    });

    const loadedTodayList = loadingRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'COMPLETED' || s === 'LOADED';
    });

    const draftLoadingList = loadingRequests.filter(r => (r.status || '').toUpperCase() === 'DRAFT');

    const pendingLoadingCount = pendingLoadingList.length;
    const pendingOffloadCount = pendingOffloadList.length;
    const loadedTodayCount = loadedTodayList.length;
    const draftLoadingCount = draftLoadingList.length;
    const completedTransfersToday = transferHistory.length;

    const totalActiveProducts = stats.totalActiveProducts ?? 0;
    const totalStockQty = stats.totalStockQty != null ? (typeof stats.totalStockQty === 'number' ? stats.totalStockQty.toLocaleString() : stats.totalStockQty) : '0';

    const totalRequestsCount = pendingLoadingCount + draftLoadingCount + loadedTodayCount + pendingOffloadCount;

    // Un-rounded floating-point ratios for a 100% seamless full circle (Sum = 100.0)
    const safeTotal = totalRequestsCount > 0 ? totalRequestsCount : 1;
    const pLoadingDash = (pendingLoadingCount / safeTotal) * 100;
    const pDraftDash = (draftLoadingCount / safeTotal) * 100;
    const loadedDash = (loadedTodayCount / safeTotal) * 100;
    const pOffloadDash = (pendingOffloadCount / safeTotal) * 100;

    const totalLoadingQtyOut = loadingRequests.reduce((sum, r) => sum + (Number(r.totalQty) || 0), 0);
    const totalOffloadQtyIn = offloadRequests.reduce((sum, r) => sum + (Number(r.totalQty) || 0), 0);

    const segments = [
        {
            id: 'pendingLoading',
            label: 'Pending Approval (Loading)',
            shortLabel: 'Pending Load',
            count: pendingLoadingCount,
            colorClass: 'text-amber-500',
            dotClass: 'bg-amber-500',
            dash: pLoadingDash,
            offset: 0,
            targetView: 'loading-requests',
            highlightClass: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-bold border-amber-200'
        },
        {
            id: 'draftLoading',
            label: 'Draft Loading Requests',
            shortLabel: 'Draft Load',
            count: draftLoadingCount,
            colorClass: 'text-blue-500',
            dotClass: 'bg-blue-500',
            dash: pDraftDash,
            offset: pLoadingDash,
            targetView: 'loading-requests',
            highlightClass: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-bold border-blue-200'
        },
        {
            id: 'loadedToday',
            label: 'Loaded Today',
            shortLabel: 'Loaded',
            count: loadedTodayCount,
            colorClass: 'text-emerald-500',
            dotClass: 'bg-emerald-500',
            dash: loadedDash,
            offset: pLoadingDash + pDraftDash,
            targetView: 'loading-requests',
            highlightClass: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-bold border-emerald-200'
        },
        {
            id: 'pendingOffload',
            label: 'Pending Verification (Offload)',
            shortLabel: 'Pending Offload',
            count: pendingOffloadCount,
            colorClass: 'text-purple-500',
            dotClass: 'bg-purple-500',
            dash: pOffloadDash,
            offset: pLoadingDash + pDraftDash + loadedDash,
            targetView: 'offload-requests',
            highlightClass: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-bold border-purple-200'
        }
    ];

    const activeSeg = segments.find(s => s.id === hoveredSegment);

    const lowStockAlerts = stats.lowStockAlerts || [];

    const recentLoading = pendingLoadingList.slice(0, 5);
    const recentOffload = pendingOffloadList.slice(0, 5);

    return (
        <div className="space-y-6">
            {/* Top Metric Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white leading-tight">{totalActiveProducts}</div>
                        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-snug">Active Products</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white leading-tight">{totalStockQty}</div>
                        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-snug">Total Stock Qty</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-3 cursor-pointer hover:border-amber-300 transition-colors" onClick={() => onNavigateView && onNavigateView('loading-requests')}>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                        <Truck className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white leading-tight">{pendingLoadingCount}</div>
                        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-snug">Pending Loading</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-3 cursor-pointer hover:border-blue-300 transition-colors" onClick={() => onNavigateView && onNavigateView('offload-requests')}>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <ArrowDownLeft className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white leading-tight">{pendingOffloadCount}</div>
                        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-snug">Pending Offload</div>
                    </div>
                </div>

                <div className="col-span-2 sm:col-span-1 bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-3 cursor-pointer hover:border-blue-300 transition-colors" onClick={() => onNavigateView && onNavigateView('transfer-history')}>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white leading-tight">{completedTransfersToday}</div>
                        <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-snug">Transfers Today</div>
                    </div>
                </div>
            </div>

            {/* Analytics Middle Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Responsive & Highly Interactive Dynamic Donut Chart */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col justify-between space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-base">Request Overview</h3>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Hover / Click Segments</span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-around gap-6 py-2">
                        {/* Interactive SVG Ring */}
                        <div className="relative w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center flex-shrink-0">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                {/* Base Track (Empty State Ring) */}
                                <circle
                                    cx="18"
                                    cy="18"
                                    r="15.9155"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                    className="text-slate-100 dark:text-slate-700/60"
                                />

                                {/* Render Active Segment Paths if Total > 0 */}
                                {totalRequestsCount > 0 && segments.map(seg => (
                                    seg.count > 0 && (
                                        <path
                                            key={seg.id}
                                            className={`${seg.colorClass} cursor-pointer transition-all duration-300`}
                                            strokeWidth={hoveredSegment === seg.id ? "5.5" : "4"}
                                            strokeDasharray={`${seg.dash}, 100`}
                                            strokeDashoffset={`-${seg.offset}`}
                                            stroke="currentColor"
                                            fill="none"
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            onMouseEnter={() => setHoveredSegment(seg.id)}
                                            onMouseLeave={() => setHoveredSegment(null)}
                                            onClick={() => onNavigateView && onNavigateView(seg.targetView)}
                                        />
                                    )
                                ))}
                            </svg>

                            {/* Reactive Center Label */}
                            <div className="absolute text-center pointer-events-none transition-all duration-200">
                                <span className={`text-2xl sm:text-3xl font-extrabold block leading-none ${activeSeg ? activeSeg.colorClass : 'text-slate-800 dark:text-white'}`}>
                                    {activeSeg ? activeSeg.count : totalRequestsCount}
                                </span>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1 truncate max-w-[90px]">
                                    {activeSeg ? activeSeg.shortLabel : 'Total'}
                                </div>
                            </div>
                        </div>

                        {/* Interactive Legend List */}
                        <div className="space-y-2 text-xs w-full sm:w-auto flex-1">
                            {segments.map(seg => {
                                const isHovered = hoveredSegment === seg.id;
                                return (
                                    <div
                                        key={seg.id}
                                        onMouseEnter={() => setHoveredSegment(seg.id)}
                                        onMouseLeave={() => setHoveredSegment(null)}
                                        onClick={() => onNavigateView && onNavigateView(seg.targetView)}
                                        className={`flex items-center justify-between gap-3 p-2 rounded-xl border transition-all cursor-pointer ${
                                            isHovered
                                                ? seg.highlightClass
                                                : 'border-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`w-2.5 h-2.5 rounded-full ${seg.dotClass} flex-shrink-0`}></span>
                                            <span className="truncate text-[11px]">{seg.label}</span>
                                        </div>
                                        <span className={`font-bold ml-auto flex-shrink-0 ${isHovered ? 'scale-110' : 'text-slate-800 dark:text-white'}`}>
                                            {seg.count}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Stock Movement Bar Chart */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-base">Stock Movement (Today)</h3>
                        <div className="flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                                <span className="w-2.5 h-2.5 bg-blue-500 rounded-sm"></span> Loading (Out)
                            </span>
                            <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                                <span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm"></span> Offloading (In)
                            </span>
                        </div>
                    </div>
                    <div className="pt-4 space-y-4">
                        <div>
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Main Warehouse</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">{totalLoadingQtyOut} out / {totalOffloadQtyIn} in</span>
                            </div>
                            <div className="h-3 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden flex">
                                <div className="bg-blue-500 h-full" style={{ width: totalLoadingQtyOut + totalOffloadQtyIn > 0 ? `${Math.round((totalLoadingQtyOut / (totalLoadingQtyOut + totalOffloadQtyIn)) * 100)}%` : '50%' }}></div>
                                <div className="bg-emerald-500 h-full" style={{ width: totalLoadingQtyOut + totalOffloadQtyIn > 0 ? `${Math.round((totalOffloadQtyIn / (totalLoadingQtyOut + totalOffloadQtyIn)) * 100)}%` : '50%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Low Stock Alerts */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            <h3 className="font-semibold text-slate-800 dark:text-white text-base">Low Stock Alerts</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${lowStockAlerts.length > 0 ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'}`}>
                                {lowStockAlerts.length}
                            </span>
                        </div>
                    </div>
                    {lowStockAlerts.length === 0 ? (
                        <div className="py-6 text-center text-xs text-slate-400">
                            No products are low in stock. All inventory levels normal.
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-700 text-xs">
                            {lowStockAlerts.map((item, idx) => (
                                <div key={idx} className="py-2.5 flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-slate-800 dark:text-slate-200">{item.name}</div>
                                        <div className="text-[10px] text-slate-400">SKU: {item.sku}</div>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-bold text-rose-500 text-sm">{item.qty}</span>
                                        <span className="text-[10px] text-slate-400 ml-1">Pcs</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Section: Real-time Consolidated Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pending Loading Requests */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span> Pending Loading Requests ({recentLoading.length})
                        </h3>
                        <button
                            onClick={() => onNavigateView && onNavigateView('loading-requests')}
                            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium flex items-center gap-1 cursor-pointer"
                        >
                            View All <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="block md:hidden p-3 space-y-3">
                        {recentLoading.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-400">No pending loading requests.</div>
                        ) : (
                            recentLoading.map((req) => (
                                <div key={req.id} className="p-3.5 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 flex items-center justify-between gap-3">
                                    <div className="space-y-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-xs text-blue-600 dark:text-blue-400">{req.requestNo}</span>
                                            <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-semibold text-[10px]">
                                                {req.vehicle || 'Van 01'}
                                             </span>
                                         </div>
                                         <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{req.salesperson}</div>
                                         <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                                             <span>Items: <strong className="text-slate-700 dark:text-slate-200">{req.itemsCount}</strong></span>
                                             <span>•</span>
                                             <span>Total Qty: <strong className="text-slate-800 dark:text-white">{req.totalQty}</strong></span>
                                         </div>
                                     </div>
                                     <button
                                         onClick={() => onViewRequest && onViewRequest(req, 'loading')}
                                         className="w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-600 shrink-0 cursor-pointer"
                                         aria-label="View Loading Request"
                                     >
                                         <Eye className="w-4 h-4" />
                                     </button>
                                 </div>
                             ))
                         )}
                     </div>

                     {/* Desktop Table View */}
                     <div className="hidden md:block overflow-x-auto">
                         <table className="w-full text-left text-xs">
                             <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 font-semibold border-b border-slate-100 dark:border-slate-700">
                                 <tr>
                                     <th className="p-3 pl-5">Request No.</th>
                                     <th className="p-3">Salesperson</th>
                                     <th className="p-3">Vehicle</th>
                                     <th className="p-3">Items</th>
                                     <th className="p-3">Total Qty</th>
                                     <th className="p-3">Action</th>
                                 </tr>
                             </thead>
                             <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                 {recentLoading.length === 0 ? (
                                     <tr>
                                         <td colSpan="6" className="p-6 text-center text-slate-400">
                                             No pending loading requests.
                                         </td>
                                     </tr>
                                 ) : (
                                     recentLoading.map((req) => (
                                         <tr key={req.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30">
                                             <td className="p-3 pl-5 font-semibold text-blue-600 dark:text-blue-400">{req.requestNo}</td>
                                             <td className="p-3 text-slate-700 dark:text-slate-300">{req.salesperson}</td>
                                             <td className="p-3">
                                                 <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-medium text-[11px]">
                                                     {req.vehicle || 'Van 01'}
                                                 </span>
                                            </td>
                                            <td className="p-3 text-slate-600 dark:text-slate-400">{req.itemsCount}</td>
                                            <td className="p-3 font-semibold text-slate-800 dark:text-white">{req.totalQty}</td>
                                            <td className="p-3">
                                                <button
                                                    onClick={() => onViewRequest && onViewRequest(req, 'loading')}
                                                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                                                    aria-label="View Request"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pending Offload Requests */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span> Pending Offload Requests ({recentOffload.length})
                        </h3>
                        <button
                            onClick={() => onNavigateView && onNavigateView('offload-requests')}
                            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium flex items-center gap-1 cursor-pointer"
                        >
                            View All <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="block md:hidden p-3 space-y-3">
                        {recentOffload.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-400">No pending offload requests.</div>
                        ) : (
                            recentOffload.map((req) => (
                                <div key={req.id} className="p-3.5 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 flex items-center justify-between gap-3">
                                    <div className="space-y-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-xs text-purple-600 dark:text-purple-400">{req.requestNo}</span>
                                            <span className="px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 font-semibold text-[10px]">
                                                {req.vehicle || 'Van 01'}
                                            </span>
                                        </div>
                                        <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{req.salesperson}</div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                                            <span>Items: <strong className="text-slate-700 dark:text-slate-200">{req.itemsCount}</strong></span>
                                            <span>•</span>
                                            <span>Total Qty: <strong className="text-slate-800 dark:text-white">{req.totalQty}</strong></span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onViewRequest && onViewRequest(req, 'offload')}
                                        className="w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-600 shrink-0 cursor-pointer"
                                        aria-label="View Offload Request"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 font-semibold border-b border-slate-100 dark:border-slate-700">
                                <tr>
                                    <th className="p-3 pl-5">Request No.</th>
                                    <th className="p-3">Salesperson</th>
                                    <th className="p-3">Vehicle</th>
                                    <th className="p-3">Items</th>
                                    <th className="p-3">Total Qty</th>
                                    <th className="p-3">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {recentOffload.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-6 text-center text-slate-400">
                                            No pending offload requests.
                                        </td>
                                    </tr>
                                ) : (
                                    recentOffload.map((req) => (
                                        <tr key={req.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30">
                                            <td className="p-3 pl-5 font-semibold text-purple-600 dark:text-purple-400">{req.requestNo}</td>
                                            <td className="p-3 text-slate-700 dark:text-slate-300">{req.salesperson}</td>
                                            <td className="p-3">
                                                <span className="px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 font-medium text-[11px]">
                                                    {req.vehicle || 'Van 01'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-slate-600 dark:text-slate-400">{req.itemsCount}</td>
                                            <td className="p-3 font-semibold text-slate-800 dark:text-white">{req.totalQty}</td>
                                            <td className="p-3">
                                                <button
                                                    onClick={() => onViewRequest && onViewRequest(req, 'offload')}
                                                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-purple-600 dark:hover:text-purple-400 cursor-pointer"
                                                    aria-label="View Request"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarehouseMainDashboard;
